import { useCallback, useMemo, useState } from 'react';
import { OrgChartEmployee } from './types';

export type OrgChartFilters = {
  departments: string[];
  locations: string[];
  teams: string[];
  position: string;
};

const EMPTY_FILTERS: OrgChartFilters = {
  departments: [],
  locations: [],
  teams: [],
  position: '',
};

const hasActiveFilters = (filters: OrgChartFilters): boolean =>
  filters.departments.length > 0 ||
  filters.locations.length > 0 ||
  filters.teams.length > 0 ||
  filters.position.trim() !== '';

const matchesFilters = (
  employee: OrgChartEmployee,
  filters: OrgChartFilters,
): boolean => {
  if (
    filters.departments.length > 0 &&
    (!employee.departmentRef ||
      !filters.departments.includes(employee.departmentRef.id))
  ) {
    return false;
  }

  if (
    filters.locations.length > 0 &&
    (!employee.locationRef ||
      !filters.locations.includes(employee.locationRef.id))
  ) {
    return false;
  }

  if (filters.teams.length > 0) {
    const employeeTeamIds = employee.teams.map((t) => t.id);
    if (!filters.teams.some((id) => employeeTeamIds.includes(id))) {
      return false;
    }
  }

  if (filters.position.trim() !== '') {
    const search = filters.position.toLowerCase().trim();
    if (!employee.position.toLowerCase().includes(search)) {
      return false;
    }
  }

  return true;
};

const addAncestors = (
  matchedIds: Set<string>,
  employeeMap: Map<string, OrgChartEmployee>,
): Set<string> => {
  const result = new Set(matchedIds);
  const queue = Array.from(matchedIds);

  while (queue.length > 0) {
    const id = queue.pop()!;
    const emp = employeeMap.get(id);
    if (emp?.manager?.id && !result.has(emp.manager.id)) {
      result.add(emp.manager.id);
      queue.push(emp.manager.id);
    }
  }

  return result;
};

export const useOrgChartFilters = (employees: OrgChartEmployee[]) => {
  const [filters, setFilters] = useState<OrgChartFilters>(EMPTY_FILTERS);

  const isActive = useMemo(() => hasActiveFilters(filters), [filters]);

  const filteredEmployees = useMemo(() => {
    if (!isActive) return employees;

    const employeeMap = new Map(employees.map((e) => [e.id, e]));

    const matchedIds = new Set<string>();
    for (const emp of employees) {
      if (matchesFilters(emp, filters)) {
        matchedIds.add(emp.id);
      }
    }

    const idsToShow = addAncestors(matchedIds, employeeMap);

    const subordinateCountByManager = new Map<string, number>();
    for (const emp of employees) {
      const managerId = emp.manager?.id;
      if (!managerId) continue;
      if (!idsToShow.has(emp.id)) continue;
      subordinateCountByManager.set(
        managerId,
        (subordinateCountByManager.get(managerId) ?? 0) + 1,
      );
    }

    return employees
      .filter((e) => idsToShow.has(e.id))
      .map((e) => ({
        ...e,
        noOfSubEmployees: subordinateCountByManager.get(e.id) ?? 0,
      }));
  }, [employees, filters, isActive]);

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  return {
    filters,
    setFilters,
    filteredEmployees,
    hasActiveFilters: isActive,
    clearFilters,
  };
};

export { matchesFilters, addAncestors, hasActiveFilters };
